import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'gatsby'
import { Container, Row, Col } from 'reactstrap'
import { Helmet } from 'react-helmet'

import SocialShare from "../../SocialShare"

import BonsPlansJeunesBreadcrumb from '../BonsPlansJeunesBreadcrumb'
import ScrollTop from '../../ScrollTop'

import { getCookiebox } from '../../../state/reducers/cookiebox'

const BonPlansJeunesSingleItem = ({ postItem, shareUrl, cookiebox }) => {
	// Validation des props
	if (!postItem || typeof postItem !== 'object') {
		return (
			<div 
				className="bons-plans-jeunes-single-item-error" 
				role="alert" 
				aria-live="polite"
				aria-describedby="bons-plans-jeunes-single-item-error-description"
			>
				<div id="bons-plans-jeunes-single-item-error-description" className="visually-hidden">
					Erreur d'affichage du bon plan jeune
				</div>
				<p>Aucun bon plan jeune disponible à afficher.</p>
			</div>
		)
	}

	const { 
		nom_dispositif, 
		thematique, 
		sous_thematique, 
		support_visuel, 
		pour_qui, 
		quelles_aides, 
		comment_en_beneficier, 
		structure_contact,
		adresse_principale,
		contact,
		site_internet,
		courriel_1,
		facebook,
		adresse_2,
		contact_2,
		courriel_2,
		adresse_3,
		contact_3,
		adresse_4,
		contact_4,
		courriel_4,
		adresse_5,
		contact_5,
		courriel_5
	} = postItem

	const title = nom_dispositif || 'Bon plan jeune sans titre'
	const ariaLabelId = 'bons-plans-jeunes-single-item-title'
	const ariaDescriptionId = 'bons-plans-jeunes-single-item-description'

	return (
		<div>
			<Helmet>
				<title>{title ? `Ville de Roubaix : ${title}` : 'Ville de Roubaix'}</title>
				<meta name="description" content={`Détails du bon plan jeune : ${title}`} />
			</Helmet>
			
			<BonsPlansJeunesBreadcrumb SingleTitle={title} />
			
			<main 
				className="bons-plans-jeunes-single-item"
				role="main"
				aria-label={`Détails du bon plan jeune : ${title}`}
				aria-labelledby={ariaLabelId}
				aria-describedby={ariaDescriptionId}
			>
				<Container>
					<div id={ariaDescriptionId} className="visually-hidden">
						Page détaillée du bon plan jeune avec informations complètes, contact et localisation
					</div>
					
					<Row>
						<Col tag="aside" lg="4" md="12" role="complementary" aria-label="Informations de contact et localisation">
							<div className="anchors-sidebar h-100 pr-5">
								<div className="bg-light h-100 bons-plans-jeunes-single-item-left">
									{adresse_principale && (
										<p className="bons-plans-jeunes-single-item-field bons-plans-jeunes-single-item-location">
											<a 
												rel="noopener noreferrer" 
												href={`http://maps.google.fr/maps?f=q&hl=fr&q=${adresse_principale}`} 
												target="_blank"
												aria-label={`Voir l'adresse ${adresse_principale} sur Google Maps (s'ouvre dans un nouvel onglet)`}
											>
												{adresse_principale}<br />
											</a>
										</p>
									)}
									
									{(contact || site_internet) && (
										<p className="bons-plans-jeunes-single-item-field bons-plans-jeunes-single-item-contact">
											{contact && <>{contact}<br /></>}
											{site_internet && (
												<a 
													rel="noopener noreferrer" 
													href={site_internet} 
													target="_blank"
													aria-label={`Site internet du bon plan (s'ouvre dans un nouvel onglet)`}
												>
													Pour plus d'infos
												</a>
											)}
										</p>
									)}
									
									<SocialShare shareUrl={shareUrl} />
								</div>
							</div>
						</Col>
						
						<Col tag="article" md="12" lg="8" className="py-md-5 page-content position-relative" role="article">
							<div className="bons-plans-jeunes-single-item-right">
								<Link 
									to="/services-infos-pratiques/jeunesse/bons-plans-pour-les-jeunes/annuaire-bons-plans-jeunes/" 
									className="bons-plans-jeunes-single-link-back d-lg-none d-md-none d-sm-block d-xs-block"
									aria-label="Retourner vers la liste des bons plans jeunes"
								>
									Retourner vers les bons plans jeunes
								</Link>
								
								{nom_dispositif && (
									<h1 
										id={ariaLabelId}
										className="bons-plans-jeunes-single-item-title"
									>
										{nom_dispositif}
									</h1>
								)}
								
								{thematique && (
									<p className="bons-plans-jeunes-single-item-content-category">
										{thematique}
									</p>
								)}
								
								{sous_thematique && (
									<p className="bons-plans-jeunes-single-item-content-subcategory">
										{sous_thematique}
									</p>
								)}
								
								{support_visuel && (
									<figure className="bons-plans-jeunes-single-item-picture" role="img" aria-label={`Image illustrant ${nom_dispositif}`}>
										<span 
											className="bons-plans-jeunes-single-item-picture-flou" 
											style={{ backgroundImage: 'url(' + support_visuel + ')' }}
											aria-hidden="true"
										></span>
										<img 
											alt={`Image illustrant ${nom_dispositif}`} 
											src={support_visuel}
											className="bons-plans-jeunes-single-item-image"
										/>
									</figure>
								)}
								
								{pour_qui && (
									<section aria-labelledby="pour-qui-label">
										<p id="pour-qui-label" className="bons-plans-jeunes-single-item-label">
											Pour qui ?
										</p>
										<p className="bons-plans-jeunes-single-item-text">
											{pour_qui}
										</p>
									</section>
								)}
								
								{quelles_aides && (
									<section aria-labelledby="quelles-aides-label">
										<p id="quelles-aides-label" className="bons-plans-jeunes-single-item-label">
											Quelle aide ?
										</p>
										<p className="bons-plans-jeunes-single-item-text">
											{quelles_aides}
										</p>
									</section>
								)}
								
								{comment_en_beneficier && (
									<section aria-labelledby="comment-beneficier-label">
										<p id="comment-beneficier-label" className="bons-plans-jeunes-single-item-label">
											Comment en bénéficier ?
										</p>
										<p className="bons-plans-jeunes-single-item-text">
											{comment_en_beneficier}
										</p>
									</section>
								)}
								
								{structure_contact && (
									<section aria-labelledby="qui-contacter-label">
										<p id="qui-contacter-label" className="bons-plans-jeunes-single-item-label">
											Qui contacter ?
										</p>
										
										<div className="bons-plans-jeunes-single-item-text">
											{structure_contact && <>{structure_contact}<br /></>}
											{adresse_principale && <>{adresse_principale}<br /></>}
											{contact && <>{contact}<br /></>}
											{courriel_1 && (
												<a 
													rel="noopener noreferrer" 
													href={`mailto:${courriel_1}`} 
													target="_blank"
													aria-label={`Envoyer un email à ${courriel_1} (s'ouvre dans un nouvel onglet)`}
												>
													{courriel_1}
												</a>
											)}
											{site_internet && (
												<a 
													rel="noopener noreferrer" 
													href={site_internet} 
													target="_blank"
													aria-label={`Site internet du bon plan (s'ouvre dans un nouvel onglet)`}
												>
													Site internet
												</a>
											)}
											{facebook && (
												<a 
													rel="noopener noreferrer" 
													href={facebook} 
													target="_blank"
													aria-label={`Page Facebook du bon plan (s'ouvre dans un nouvel onglet)`}
												>
													Page facebook
												</a>
											)}
										</div>
										
										{adresse_2 && (
											<div className="bons-plans-jeunes-single-item-text">
												{adresse_2 && <>{adresse_2}<br /></>}
												{contact_2 && <>{contact_2}<br /></>}
												{courriel_2 && (
													<a 
														rel="noopener noreferrer" 
														href={`mailto:${courriel_2}`} 
														target="_blank"
														aria-label={`Envoyer un email à ${courriel_2} (s'ouvre dans un nouvel onglet)`}
													>
														{courriel_2}
													</a>
												)}
											</div>
										)}
										
										{adresse_3 && (
											<div className="bons-plans-jeunes-single-item-text">
												{adresse_3 && <>{adresse_3}<br /></>}
												{contact_3 && <>{contact_3}<br /></>}
												{courriel_2 && (
													<a 
														rel="noopener noreferrer" 
														href={`mailto:${courriel_2}`} 
														target="_blank"
														aria-label={`Envoyer un email à ${courriel_2} (s'ouvre dans un nouvel onglet)`}
													>
														{courriel_2}
													</a>
												)}
											</div>
										)}
										
										{adresse_4 && (
											<div className="bons-plans-jeunes-single-item-text">
												{adresse_4 && <>{adresse_4}<br /></>}
												{contact_4 && <>{contact_4}<br /></>}
												{courriel_4 && (
													<a 
														rel="noopener noreferrer" 
														href={`mailto:${courriel_4}`} 
														target="_blank"
														aria-label={`Envoyer un email à ${courriel_4} (s'ouvre dans un nouvel onglet)`}
													>
														{courriel_4}
													</a>
												)}
											</div>
										)}
										
										{adresse_5 && (
											<div className="bons-plans-jeunes-single-item-text">
												{adresse_5 && <>{adresse_5}<br /></>}
												{contact_5 && <>{contact_5}<br /></>}
												{courriel_5 && (
													<a 
														rel="noopener noreferrer" 
														href={`mailto:${courriel_5}`} 
														target="_blank"
														aria-label={`Envoyer un email à ${courriel_5} (s'ouvre dans un nouvel onglet)`}
													>
														{courriel_5}
													</a>
												)}
											</div>
										)}
									</section>
								)}
							</div>
							
							<ScrollTop />
						</Col>
					</Row>
				</Container>
			</main>
		</div>
	)
}

const mapStateToProps = state => ({
	cookiebox: getCookiebox(state)
})

export default connect(mapStateToProps)(BonPlansJeunesSingleItem)
